import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import io from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth.service';
import { EnvService } from '../env.service';
import { TenantService } from '../shared/services/tenant/tenant.service';

@Injectable({
  providedIn: 'root',
})
export class SocketsService implements OnDestroy {
  private socket;
  private todoEventSource = new BehaviorSubject<any>({});
  todoEvents$ = this.todoEventSource.asObservable();
  private streamSource = new BehaviorSubject<any>({});
  streamEvent$ = this.streamSource.asObservable();
  private messageSource = new BehaviorSubject<any>({});
  messageEvent$ = this.messageSource.asObservable();
  private conferenceSource = new BehaviorSubject<any>({});
  conferenceEvent$ = this.conferenceSource.asObservable();
  private videoCallEvenSource = new BehaviorSubject<any>({});
  videoCallEvent$ = this.videoCallEvenSource.asObservable();
  private routeSub: Subscription;
  joinedRooms = [];

  constructor(
    private authService: AuthService,
    private env: EnvService,
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService
  ) {
    this.routeSub = this.activatedRoute.queryParams.subscribe((params) => {
      if (this.socket) {
        this.closeSocket();
      }
    });
  }
  ngOnDestroy() {
    this.routeSub.unsubscribe();
  }
  async initSocket() {
    if (this.socket && this.socket.connected) {
      return this.socket;
    }
    return new Promise(async (resolve, reject) => {
      const { token } = await this.authService.getValidToken();

      const queryParams: any = { appName: this.env.appName };
      const tenantId = this.tenantService.activeTenantId() || localStorage.getItem('active_workspace');
      if (tenantId) {
        queryParams.tenantId = tenantId;
      }

      this.socket = (io as any)(environment.mediaServerUrl, {
        reconnection: false,
        auth: {
          token: token,
        },
        query: queryParams,
      });

      this.socket.on('connect', (data) => {
        resolve(data);
      });

      this.socket.on('connect_error', (error) => {
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        this.passStreamEvent({ type: 'disconnected', data: reason });
      });

      this.socket.on('todo-event', (data) => {
        this.passTodoEvent(data);
      });

      this.socket.on('call-user-list', (data) => {
        this.passVideoCallEvent({ event: 'call-user-list', data });
      });
      this.socket.on('new-user-in-call', (data) => {
        this.passVideoCallEvent({ event: 'new-user-in-call', data });
      });
      this.socket.on('chat-message', (data) => {
        this.passMessageEvent(data.event);
      });
      const conferenceEvents = ['conference-join'];

      for (const event of conferenceEvents) {
        this.socket.on(event, (data) => {
          this.passStreamEvent({
            type: event,
            data,
          });
        });
      }

      const streamEvents = [
        'user-left',
        'stream-track-info',
        'screen-share-status',
        'stream-status',
        'offer',
        'answer',
        'stream-ice-candidate',
        'stream-started',
        'stream-watch-request',
        'stream-status',
        'connection-to-stream-complete',
        'stream-ended-notification',
        'stream-error',
        'connection-active',
        'stream-end',
        'transcription-data',
        'transcription-interim-data',
        'stopped-stream',
        'final-transcription',
        'curate-transcription-done',
        'curate-transcription-error',
        'call-ice-candidate',
        'call-offer',
        'call-answer',
        'caller-status',
        'end-call',
        'remote-track-information',
        'call-transcription-data',
        'call-transcription-interim-data',
        'screen-recording-ended',
      ];

      for (const streamEvent of streamEvents) {
        this.socket.on(streamEvent, (data) => {
          this.passStreamEvent({
            type: streamEvent,
            data,
          });
        });
      }
    });
  }
  closeSocket() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }
  passVideoCallEvent(event) {
    this.videoCallEvenSource.next(event);
  }

  passTodoEvent(event) {
    this.todoEventSource.next(event);
  }

  passStreamEvent(event) {
    this.streamSource.next(event);
  }

  passConferenceEvent(event) {
    this.conferenceSource.next(event);
  }

  passMessageEvent(event) {
    this.messageSource.next(event);
  }

  getId() {
    if (this.socket) {
      return this.socket.id;
    } else {
      return null;
    }
  }
  emitEvent(type, data) {
    // data.bearer = localStorage.getItem("apiToken");
    this.socket.emit(type, data);
  }

  onEvent(type: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(type, callback);
    }
  }

  offEvent(type: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      if (callback) {
        this.socket.off(type, callback);
      } else {
        this.socket.off(type);
      }
    }
  }
}
